import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatDetail } from "@/lib/api";
import { toast } from "sonner";

export default function ExerciseEditor({ open, onOpenChange, exercise, section, onSaved }) {
  const isEdit = !!exercise;
  const [name, setName] = useState("");
  const [sets, setSets] = useState(3);
  const [reps, setReps] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  useEffect(() => {
    if (open) {
      setName(exercise?.name ?? "");
      setSets(exercise?.sets ?? 3);
      setReps(exercise?.reps ?? "");
      setConfirmDel(false);
    }
  }, [open, exercise]);

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim() || !reps.trim()) return;
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/workouts/exercise/${exercise.id}`, {
          name: name.trim(),
          sets: Number(sets),
          reps: reps.trim(),
        });
        toast.success("Exercise updated");
      } else {
        await api.post("/workouts/exercise", {
          section,
          name: name.trim(),
          sets: Number(sets),
          reps: reps.trim(),
        });
        toast.success("Exercise added");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!isEdit) return;
    if (!confirmDel) {
      setConfirmDel(true);
      return;
    }
    setBusy(true);
    try {
      await api.delete(`/workouts/exercise/${exercise.id}`);
      toast.success("Exercise removed");
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error("Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="!bg-[#0F0F16] !border-cyan-400/30 !rounded-none max-w-md"
        data-testid="exercise-editor-dialog"
      >
        <DialogHeader>
          <DialogTitle asChild>
            <div>
              <div className="label-tech mb-1">{isEdit ? "Modify Exercise" : "New Exercise"}</div>
              <div className="h-display text-xl">{isEdit ? exercise.name : "Add Exercise"}</div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={save} className="space-y-4 mt-2">
          <div>
            <label className="label-tech block mb-1.5">Name</label>
            <input
              data-testid="exercise-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Push-ups"
              maxLength={60}
              autoFocus
              className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none px-3 py-2.5 text-white text-sm font-mono-tech"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-tech block mb-1.5">Sets</label>
              <input
                data-testid="exercise-sets-input"
                type="number"
                min={1}
                max={50}
                value={sets}
                onChange={(e) => setSets(e.target.value)}
                className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none px-3 py-2.5 text-white text-sm font-mono-tech"
              />
            </div>
            <div>
              <label className="label-tech block mb-1.5">Reps / Time</label>
              <input
                data-testid="exercise-reps-input"
                value={reps}
                onChange={(e) => setReps(e.target.value)}
                placeholder="8-10 or 45s"
                maxLength={40}
                className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none px-3 py-2.5 text-white text-sm font-mono-tech"
              />
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              data-testid="exercise-save-btn"
              type="submit"
              disabled={busy}
              className="btn-neon flex-1"
            >
              {busy ? "..." : isEdit ? "Save Changes" : "Add Exercise"}
            </button>
            {isEdit && (
              <button
                data-testid="exercise-delete-btn"
                type="button"
                onClick={doDelete}
                disabled={busy}
                className={`btn-purple ${confirmDel ? "!border-red-500 !text-red-400" : ""}`}
              >
                {confirmDel ? "Confirm?" : "Delete"}
              </button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
